import config from '../config';

// 定义WebSocket消息类型常量
export const WS_MESSAGE_TYPES = {
  CHAT_MESSAGE: 'chat_message',
  MESSAGE_ACK: 'message_ack',
  SESSION_UPDATE: 'session_update',
  HEARTBEAT: 'heartbeat'
};

class WebSocketManager {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 3000; // 3秒
    this.messageListeners = [];
    this.connectionListeners = [];
    this.heartbeatInterval = null;
    this.pendingMessages = []; // 初始化待发送消息数组
    this.token = null;
    this.reconnectTimer = null;
  }

  // 连接WebSocket
  connect(token) {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      // WebSocket已经连接或正在连接中
      return;
    }

    if (!token) {
      return;
    }

    this.token = token;
    this.createWebSocketConnection();
  }

  createWebSocketConnection() {
    try {
      // 获取WebSocket协议
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      
      // 获取WebSocket主机
      const wsHost = process.env.REACT_APP_WS_HOST || window.location.host;
      
      // 构建WebSocket URL
      const wsUrl = `${wsProtocol}//${wsHost}/ws/user?satoken=${this.token}`;
      
      // 创建WebSocket连接
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = (event) => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        
        // 通知连接状态监听器
        this.notifyConnectionListeners(true);
        
        // 开始心跳检测
        this.startHeartbeat();
        
        // 发送所有待发送的消息
        if (this.pendingMessages && this.pendingMessages.length > 0) {
          this.pendingMessages.forEach(message => {
            this.doSendMessage(message);
          });
          this.pendingMessages = [];
        }
        
        // 发送会话刷新事件
        this.notifyMessageListeners({ type: WS_MESSAGE_TYPES.SESSION_UPDATE });
      };

      this.socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this.notifyMessageListeners(message);
        } catch (error) {
          console.error('解析WebSocket消息失败:', error);
        }
      };

      this.socket.onclose = (event) => {
        this.isConnected = false;
        this.stopHeartbeat();
        this.notifyConnectionListeners(false);
        
        // 尝试重新连接
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          
          // 使用指数退避策略，每次重连的间隔时间逐渐增加
          const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000);
          
          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
          }
          
          this.reconnectTimer = setTimeout(() => {
            this.createWebSocketConnection();
          }, delay);
        }
      };

      this.socket.onerror = (error) => {
        console.error('WebSocket连接错误:', error);
      };
    } catch (error) {
      console.error('创建WebSocket连接失败:', error);
    }
  }

  // 发送消息
  sendMessage(message) {
    if (!this.isConnected) {
      console.error('WebSocket未连接，无法发送消息');
      
      // 存储消息，等连接建立后发送
      if (!this.pendingMessages) {
        this.pendingMessages = [];
      }
      this.pendingMessages.push(message);
      
      return false;
    }

    return this.doSendMessage(message);
  }

  doSendMessage(message) {
    try {
      this.socket.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error('发送WebSocket消息失败:', error);
      return false;
    }
  }

  // 关闭连接
  disconnect() {
    if (this.socket) {
      this.stopHeartbeat();
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      this.socket.close();
      this.socket = null;
      this.isConnected = false;
    }
  }

  // 添加消息监听器
  addMessageListener(listener) {
    this.messageListeners.push(listener);
  }

  // 移除消息监听器
  removeMessageListener(listener) {
    this.messageListeners = this.messageListeners.filter(l => l !== listener);
  }

  // 添加连接状态监听器
  addConnectionListener(listener) {
    this.connectionListeners.push(listener);
  }

  // 移除连接状态监听器
  removeConnectionListener(listener) {
    this.connectionListeners = this.connectionListeners.filter(l => l !== listener);
  }

  // 通知所有消息监听器
  notifyMessageListeners(message) {
    this.messageListeners.forEach(listener => {
      try {
        listener(message);
      } catch (error) {
        console.error('执行消息监听器失败:', error);
      }
    });
  }

  // 通知所有连接状态监听器
  notifyConnectionListeners(isConnected) {
    this.connectionListeners.forEach(listener => {
      try {
        listener(isConnected);
      } catch (error) {
        console.error('执行连接状态监听器失败:', error);
      }
    });
  }

  // 开始心跳检测
  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected) {
        this.sendMessage({ type: 'heartbeat', data: { timestamp: Date.now() } });
      }
    }, 30000); // 30秒发送一次心跳
  }

  // 停止心跳检测
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }
}

// 创建单例
const websocketManager = new WebSocketManager();
export default websocketManager; 